import React, { useContext } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import moment from 'moment';
import * as pluralize from 'pluralize';
import { CListGroupItem, CTooltip, CImg } from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { Job } from "../../shared/models/jobs";
import { formatDuration } from '../../shared/utils/time-utils';
import tooltip from '../../assets/images/tool_tip.svg';
import { MapViewerInvite } from '../../shared/components/Glympser/Glympser';
import { ConfigContext } from '../../shared/contexts/journeyConfig.context';
import { LocationStatus } from '../../shared/models/map';

const LocationShareIcon = [
  '24 24', 
  `<path d="M9 5.48555C9 5.76169 9.22461 5.9837 9.50007 6.0031C12.9755 6.24791 15.7521 9.02452 15.9969 12.5C16.0163 12.7754 16.2384 13 16.5145 13C16.7906 13 17.0161 12.7756 16.9991 12.4999C16.7511 8.472 13.528 5.24888 9.50009 5.0009C9.22447 4.98393 9 5.20941 9 5.48555Z" fill="current"/>
  <path d="M11 1.4883C11 1.76445 11.2242 1.98686 11.5 2.00196C16.0725 2.25232 19.736 5.91583 19.9863 10.4883C20.0014 10.7641 20.2239 10.9883 20.5 10.9883C20.7761 10.9883 21.0013 10.7642 20.9877 10.4884C20.7354 5.3634 16.6249 1.25291 11.4999 1.00058C11.2241 0.987004 11 1.21216 11 1.4883Z" fill="current"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M8 9C10.7614 9 13 11.2386 13 14C13 17.0069 11.7311 19.3579 9.19345 21.6252C8.51282 22.2334 7.48718 22.2334 6.80655 21.6252C4.26885 19.3579 3 17.0069 3 14C3 11.2386 5.23858 9 8 9ZM8 16C9.10457 16 10 15.1046 10 14C10 12.8954 9.10457 12 8 12C6.89543 12 6 12.8954 6 14C6 15.1046 6.89543 16 8 16Z" fill="current"/>
`];

const PHASE_STATUS = {
  quasi: {
    title : "On the way",
    titleIcon : "cil-cursor",
    tooltip: "Driver is en route."
  },
  live: {
    title : "On the way",
    titleIcon : "cil-cursor",
    tooltip: "Driver is en route."
  },
  arrived: {
    title : "Arrived",
    titleIcon : "cil-clock",
    tooltip: "Driver is arrived."
  },
  feedback: {
    title : "Completed",
    titleIcon : "cil-clock",
    tooltip: "Delivery is completed."
  }
}

interface LiveMapActiveDriversProps {
  jobs: Job[];
  selectedJob: Job;
  onSelect: (job: Job) => void;
  getJobAgentName: (agentId: number) => string;
  invitesData: MapViewerInvite[];
}

const LiveMapActiveDrivers = (props: LiveMapActiveDriversProps) => {
  const {
    jobs,
    selectedJob,
    onSelect,
    getJobAgentName,
    invitesData,
  } = props;
  const {
    liveMapFilter,
  } = useFlags();
  const labels = useContext(ConfigContext);

  return <>{
    jobs?.length > 0 ? jobs?.map((activeJob) => {
      const name = getJobAgentName(activeJob?.agent_id);
      const etaMetadataValue = activeJob?.metadata?.find(m => m?.n === 'eta')?.v as any;
      const metadataFilter = activeJob?.metadata?.find(m => m?.n === liveMapFilter);
      const inviteData = invitesData?.find((i) => i.id === activeJob?.invite_code);
      const eta = etaMetadataValue ? (etaMetadataValue?.eta + etaMetadataValue?.eta_ts - Date.now()) : inviteData?.eta*1000;
      const phaseContent = activeJob?.phase === 'arrived' ?
          <span>{PHASE_STATUS[activeJob.phase]?.title} at: {moment(activeJob?.arrived_time).format("hh:mm A")}</span>
          : PHASE_STATUS[activeJob.phase]?.title;
  
      let locationContent = 'Sharing location'
      let locationStatus: LocationStatus = 'sharing';
      const locationVisibility = inviteData?.custom?.find(m => m?.n === 'visibility')?.v?.location as any;
      const isLocationVisible = locationVisibility && (locationVisibility === 'visible' || locationVisibility === 'cloaked');

      if (!inviteData?.location) {
        locationStatus = 'not-sharing';
        locationContent = 'Not sharing location';
      } else if (inviteData?.location && (!isLocationVisible || inviteData?.lastUpdateTime > 300)) {
        locationStatus = 'not-sharing';
        locationContent = `No current location (Last updated ${moment(new Date(Date.now() - inviteData?.lastUpdateTime * 1000))?.format('MM/DD/YYYY hh:mm A')})`;
      } else if (inviteData?.location && !inviteData?.eta) {
        locationStatus = 'eta-unavailable';
        locationContent = 'ETA Unavailable';
      }
      const isSharingLocation = locationStatus === 'sharing';

      const isValidEta = eta != null && Number.isFinite(eta) && eta > 0;
      let etaDisplay = 'ETA Unavailable';
      if (locationStatus === 'not-sharing') {
        etaDisplay = 'Not Sharing Location';
      } else if (isValidEta) {
        etaDisplay = formatDuration(moment.duration(eta, 'ms')) || 'Less than a minute';
      }
  
      return <CListGroupItem  key={activeJob._id} onClick={() => onSelect(activeJob)} className={`cursor-pointer ${activeJob?.invite_code === selectedJob?.invite_code ? 'active' : ''}`}>
        <div>
        <div className="d-flex align-items-center flex-nowrap">
            <CTooltip placement="top" content={locationContent}>
              <CIcon content={LocationShareIcon} className={`flex-shrink-0 mfe-2 ${isSharingLocation ? 'color-blue' : 'color-red'}`} />
            </CTooltip>
            <span className="font-weight-bold text-truncate">{name} {metadataFilter && `(${metadataFilter?.v})`}</span>
          </div>
          <div className="d-flex align-items-center">
            <CIcon name="cil-description" className="flex-shrink-0 mfe-2" />
            <span className="text-truncate">Description: {activeJob.description}</span>
          </div>
          <div className="d-flex align-items-center">
            <CIcon name={PHASE_STATUS[activeJob.phase]?.titleIcon} className="flex-shrink-0 mfe-2" />
            <span>{phaseContent}</span>
            <CTooltip placement="top"
                      content={PHASE_STATUS[activeJob.phase]?.tooltip}>
              <CImg src={tooltip} className="ml-2 glympser-toolTip" />
            </CTooltip>
          </div>
          {
            activeJob?.phase !== 'arrived' && (
              <div className="d-flex align-items-center">
                <CIcon name="cil-clock" className="flex-shrink-0 mfe-2" />
                <span className="text-truncate">ETA:
                  <span className="font-weight-bold">
                    {etaDisplay}
                  </span>
                </span>
              </div>
            )
          }
        </div>
      </CListGroupItem>
    }) : <div className="font-weight-bold">No Active {pluralize(labels.task)}</div>
  }</>;
};

export default LiveMapActiveDrivers;